// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — one round of a live race, decoupled from Trystero, the DOM and the clock (driven by
// play()/tick()), which is what makes the host-transfer contract testable without a network.
//
// PARALLEL SAME-SEED RACE. Every peer generates its OWN copy of a yard that is a pure function of
// the frozen round seed, shunts it, and reaches its OWN end (train made up, gave up, or the round
// clock ran out) with no host help at all. NO YARD STATE EVER CROSSES THE WIRE — only a tiny
// status gossip: moves so far and wagons seated as live progress, and on finishing, the final
// move count and time. HOST TRANSFER IS THEREFORE A PURE DISPLAY CONCERN, and onHostChange is
// wired regardless, because a game that has never been asked what happens when the host leaves is
// a game that freezes when the host leaves.
//
// The winner is whoever makes the train up in the FEWEST MOVES, ties broken by the clock. Because
// par is the proven optimum and every peer shunts the identical yard, the results screen can say
// exactly how far off the best possible line each player finished — including the players who did
// not finish at all.

import { drainEvents, giveUp as giveUpGame, play as playGame, tick as tickGame, type GameEvent, type GameState } from './game';
import { createGame } from './game';
import type { Mode } from './modes';
import type { Move } from './rules';

export interface PeerResult {
  seat: number;
  name: string;
  /** Move count — the score. */
  moves: number;
  /** Wagons correctly seated at the mouth of the departure road (live progress). */
  seated: number;
  /** How many times they backed a move out. Shown, never scored. */
  undos: number;
  done: boolean;
  /** Finished by making the train up (as opposed to giving up or timing out). */
  won: boolean;
  ms: number;
  left: boolean;
}

/** The whole wire protocol. Numbers only — never a yard, never a move. */
export interface RaceMsg {
  m?: number; // moves so far
  s?: number; // wagons seated
  u?: number; // undos
  d?: boolean; // done
  w?: boolean; // finished successfully
  fm?: number; // final moves
  ms?: number; // finish time
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a window. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export class Race {
  readonly state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  readonly par: number;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.par = this.state.par;
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        moves: 0,
        seated: 0,
        undos: 0,
        done: false,
        won: false,
        ms: 0,
        left: false,
      });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  private syncSelf(): void {
    const me = this.self();
    if (!me) return;
    me.moves = this.state.moves;
    me.seated = this.state.seated;
    me.undos = this.state.undos;
  }

  /** Play one move. Spectators no-op. */
  play(m: Move): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    playGame(this.state, m);
    const events = drainEvents(this.state);
    this.syncSelf();
    this.deps.send({ m: this.state.moves, s: this.state.seated, u: this.state.undos });
    this.deps.onChange();
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  /** Back a move out. Free, and gossiped so rivals see the move count fall back honestly. */
  undo(fn: (g: GameState) => boolean): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    if (!fn(this.state)) return [];
    const events = drainEvents(this.state);
    this.syncSelf();
    this.deps.send({ m: this.state.moves, s: this.state.seated, u: this.state.undos });
    this.deps.onChange();
    return events;
  }

  giveUp(): void {
    if (this.selfSeat === null || this.state.over) return;
    giveUpGame(this.state);
    drainEvents(this.state);
    this.endSelf();
  }

  /** Advance the wall clock. Running out ends this peer's round, so a race always terminates. */
  tick(dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    tickGame(this.state, dtMs);
    const events = drainEvents(this.state);
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.self();
    if (me) {
      me.done = true;
      me.won = this.state.finished;
      me.moves = this.state.moves;
      me.seated = this.state.seated;
      me.undos = this.state.undos;
      me.ms = Math.round(this.state.elapsedMs);
    }
    this.deps.send({
      d: true,
      w: this.state.finished,
      fm: this.state.moves,
      ms: Math.round(this.state.elapsedMs),
      s: this.state.seated,
      u: this.state.undos,
    });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return;
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.m === 'number') r.moves = Math.max(0, msg.m | 0);
    if (typeof msg.s === 'number') r.seated = Math.max(0, msg.s | 0);
    if (typeof msg.u === 'number') r.undos = Math.max(0, msg.u | 0);
    if (msg.d) {
      r.done = true;
      r.won = !!msg.w;
      if (typeof msg.fm === 'number') r.moves = Math.max(0, msg.fm | 0);
      if (typeof msg.ms === 'number') r.ms = Math.max(0, msg.ms | 0);
    }
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.done) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.done && !r.left) return false;
    return true;
  }

  /** The player who takes the round: fewest moves among those who made the train up. */
  winner(): PeerResult | null {
    const top = this.ranked().find((r) => !r.left);
    return top && top.won ? top : null;
  }

  /**
   * Finishers first (fewest moves, then fastest), then everyone still shunting or out (more
   * wagons seated first), then anyone who left.
   */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      const rank = (r: PeerResult): number => (r.won ? 0 : r.done ? 1 : 2);
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (ra === 0) return a.moves - b.moves || a.ms - b.ms;
      return b.seated - a.seated || a.moves - b.moves || a.seat - b.seat;
    });
  }
}
